import { Col, Row, Modal, Form, Tag, Popover, Tooltip } from 'antd'
import { globalConfirm } from 'components/elements/global'
import AssignAlertModal from 'components/shared/Modals/AssignAlertModal'
import { leadApi, useWithdrawAlertLeadMutation } from 'features/leads/api'
import { TAlertLabel, TALertSimple } from 'features/leads/types'
import { useAppDispatch } from 'hooks'
import { FC, useEffect, useMemo, useState } from 'react'
import {
 TAlertControlType,
 TAlertFeedType,
 TAlertStatus,
 TPutAlertType,
 useControlAlertMutation,
} from 'features/alert'
import AlertFormSection from 'features/alert/components/AlertFormSection'
import { BaseButton } from 'components/elements/BaseButton'
import ProInnerCard from '../../ProInnerCard'
import SimpleBlockItem from '../../SimpleBlockItem'

const getAlertMsg = (label?: TAlertLabel) => {
 switch (label) {
  case 'Self':
   // eslint-disable-next-line quotes
   return "This alert was set by the lead. Editing it may override the lead's original preferences. Are you sure you want to continue?"
  case 'Smart':
   return 'Editing this Smart Alert will disable automatic adjustments based on lead activity. Are you sure you want to continue?'
  default:
   return 'Are you sure you want to edit this Alert?'
 }
}

const getAlertDeleteMsg = (label?: TAlertLabel) => {
 switch (label) {
  case 'Self':
   // eslint-disable-next-line quotes
   return 'This alert was set by the lead. Are you sure you want to continue?'
  case 'Smart':
   return 'This alert was added based on leads activity. Once deleted, it cannot be reverted. Are you sure you want to continue?'
  default:
   return 'Are you sure want remove Alert from this Lead?'
 }
}

const getAlertControlMsg = (label: TAlertLabel, sts: TAlertStatus) => {
 const stgMsg = sts === 'Active' ? 'Pausing' : 'Activating'
 switch (label) {
  case 'Self':
   // eslint-disable-next-line quotes
   return `This alert was set by the lead. ${stgMsg} it may override the lead's original preferences. Are you sure you want to continue?`
  case 'Smart':
   return 'This alert was added based on leads activity. Are you sure you want to continue?'
  default:
   return `Are you sure want to ${stgMsg} this Alert?`
 }
}

const AlertBox: FC<{
 controlType?: TAlertControlType
 nla?: TALertSimple[]
 leadId?: any
 isTransferred?: boolean
 type: TAlertFeedType
}> = ({ nla, leadId, isTransferred, type, controlType }) => {
 const [showModal, setShowModal] = useState(false)
 //  const [newStatus, setStatus] = useState(status)
 const [showViewModal, setShowViewModal] = useState<{
  show: boolean
  data?: TALertSimple
 }>({ show: false })

 const [showEditModal, setShowEditModal] = useState<{
  show: boolean
  data?: TALertSimple
 }>({ show: false })

 const d = useAppDispatch()

 const [withdrawAlertLead] = useWithdrawAlertLeadMutation()

 const refreshLead = () => d(leadApi.util.invalidateTags(['LEAD']))

 const handleRemoveAlert = (id: any, aLabel: TAlertLabel) => {
  globalConfirm({
   content: getAlertDeleteMsg(aLabel),
   onOk: async () => {
    await withdrawAlertLead({ lead_id: leadId, alert_id: id }).unwrap()
    refreshLead()
   },
  })
 }

 const title = useMemo(() => {
  switch (type) {
   case 'forsale':
    return 'New Listing Alert'
   case 'openhouse':
    return 'Open House Alert'
   case 'sold':
    return 'Neighbourhood Alert'
   case 'price-drop':
    return 'Price Drop Alert'
   default:
    return ''
  }
 }, [type])

 const [form] = Form.useForm<
  TPutAlertType & { priceRange?: { min?: any; max?: any } }
 >()

 //  const HandleControlAlert = async (
 //   action: TAlertStatus,
 //   alert_id?: any,
 //   leadId?: any,
 //  ) => {
 //   globalConfirm({
 //    content: `Are you sure you want to ${action} Listing Alert for the selected lead(s)?`,
 //    onOk: async () => {
 //     await controlAction({
 //      action,
 //      alert_id,
 //      type: controlType ?? 'new_listing_alert',
 //      lead_id: [leadId],
 //     }).unwrap()
 //     // queryClient.invalidateQueries(`get-alert-leads-${id}-${type}-${searchKey}`)
 //     // queryClient.invalidateQueries('get-alerts')
 //     // setApActionType(null)
 //     // onCancel?.()
 //     // refetch?.()
 //    },
 //   })
 //  }

 return (
  <div>
   <ProInnerCard
    emptyText={`${title} is Empty`}
    isEmpty={!nla || nla?.length === 0}
    onCLickAdd={isTransferred ? () => setShowModal(true) : undefined}
    title={title}
   >
    <Row gutter={[15, 15]}>
     {nla &&
      nla.map((item, i) => (
       <Col span={24} key={i}>
        <SimpleBlockItem
         tooltip={{
          edit: 'Edit Alert',
          close: 'Delete Alert',
          view: 'View Alert',
          // delete: 'Delete',
         }}
         actions={[
          <AlertControlButton
           onSuccess={refreshLead}
           controlType={controlType}
           item={item}
           leadId={leadId}
           key="alert-control"
          />,
         ]}
         onEdit={() => {
          globalConfirm({
           okText: 'Continue',
           content: getAlertMsg(item.label),
           onOk: () => {
            setShowEditModal({ show: true, data: item })
           },
          })
         }}
         onClose={
          isTransferred
           ? () => handleRemoveAlert(item.id, item.label ?? 'Agent')
           : undefined
         }
         text={
          <div className="d-flex">
           {item.name}{' '}
           <Tag
            color="green"
            style={{ marginLeft: '6px', borderRadius: '6px' }}
           >
            {item.label}
            <Tooltip
             overlay={`This alert was set by ${(() => {
              if (item.label === 'Agent') return 'you'
              if (item.label === 'Smart') return 'the system'
              return 'the lead'
             })()}.`}
            >
             <i className="fas fa-info-circle mt-1 ms-1" />
            </Tooltip>
           </Tag>
          </div>
         }
         onView={
          isTransferred
           ? () => {
              setShowViewModal({ show: true, data: item })
              form.setFieldsValue({})
             }
           : undefined
         }
        />
       </Col>
      ))}
    </Row>
   </ProInnerCard>
   {showModal && (
    <AssignAlertModal
     type={type}
     leadId={leadId}
     onSuccess={refreshLead}
     onCancel={() => setShowModal(false)}
     visible={showModal}
    />
   )}

   {showViewModal?.show && (
    <Modal
     footer={null}
     width={1100}
     title={`View ${title}`}
     onCancel={() => {
      setShowViewModal({ show: false })
      form.resetFields()
     }}
     visible={showViewModal?.show}
    >
     <AlertFormSection
      show={showViewModal?.show}
      form={form}
      type={type}
      mode="EDIT"
      editData={showViewModal?.data as any}
      isReadOnly
     />
    </Modal>
   )}

   {showEditModal?.show && (
    <Modal
     footer={null}
     width={1100}
     title={`Edit ${title}`}
     onCancel={() => {
      setShowEditModal({ show: false })
      form.resetFields()
     }}
     visible={showEditModal?.show}
    >
     <AlertFormSection
      show={showEditModal?.show}
      form={form}
      type={type}
      mode="EDIT"
      refresh={refreshLead}
      onCancel={() => {
       setShowEditModal({ show: false })
       form.resetFields()
      }}
      editData={showEditModal?.data as any}
     />
    </Modal>
   )}
  </div>
 )
}

export default AlertBox

const AlertControlButton: FC<{
 leadId?: any
 item: TALertSimple
 onSuccess?: () => void
 controlType?: TAlertControlType
}> = ({ controlType, onSuccess, item, leadId }) => {
 const [status, setStatus] = useState<TAlertStatus>(item.status ?? 'Active')

 const [controlAction, { isLoading: controlActionLoading }] =
  useControlAlertMutation()

 const handleControlAlert = (
  status: TAlertStatus,
  labl: TAlertLabel,
  alert_id: any,
  leadId: any,
 ) => {
  globalConfirm({
   content: getAlertControlMsg(labl, status),
   onOk: async () => {
    await controlAction({
     action: status === 'Active' ? 'Pause' : 'Active',
     alert_id,
     type: controlType ?? 'new_listing_alert',
     lead_id: [leadId],
    }).unwrap()
    setStatus(status === 'Active' ? 'Pause' : 'Active')
    // await withdrawAlertLead({ lead_id: leadId, alert_id: action }).unwrap()
    onSuccess?.()
   },
  })
 }

 useEffect(() => {
  setStatus(item.status ?? 'Active')
 }, [item.status])

 return (
  <BaseButton
   tooltipText={status === 'Active' ? 'Pause Alert' : 'Resume Alert'}
   key="alert-control"
   onClick={() =>
    handleControlAlert(
     status ?? 'Active',
     item.label ?? 'Agent',
     item.id,
     leadId,
    )
   }
   buttonStyle="outline"
   size="small"
   buttonType="warning"
   icon={
    <i className={`fas ${status === 'Active' ? 'fa-pause' : 'fa-play'}`} />
   }
  />
 )
}
